import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon } from 'lucide-react';
const privacyContent = {
  title: 'Política de Privacidad',
  sections: [{
    heading: 'Información que Recopilamos',
    text: 'Recopilamos información personal que usted nos proporciona directamente, como su nombre y dirección de correo electrónico al realizar una compra. También recopilamos información de pago a través de nuestros procesadores de pago seguros de terceros.'
  }, {
    heading: 'Uso de la Información',
    text: 'Utilizamos su información personal para procesar sus pedidos, enviarle el producto digital adquirido, comunicarnos con usted sobre su compra y mejorar nuestros servicios. No vendemos ni compartimos su información personal con terceros para fines de marketing.'
  }, {
    heading: 'Protección de Datos',
    text: 'Implementamos medidas de seguridad técnicas y organizativas para proteger su información personal contra acceso no autorizado, pérdida o alteración. Todos los pagos se procesan a través de plataformas seguras con encriptación SSL.'
  }, {
    heading: 'Cookies',
    text: 'Nuestro sitio web puede utilizar cookies para mejorar su experiencia de navegación y analizar el tráfico del sitio. Puede configurar su navegador para rechazar cookies, aunque esto puede afectar la funcionalidad del sitio.'
  }, {
    heading: 'Sus Derechos',
    text: 'Usted tiene derecho a acceder, rectificar o eliminar su información personal en cualquier momento. Para ejercer estos derechos, contáctenos a través de nuestro correo electrónico de soporte.'
  }, {
    heading: 'Cambios en esta Política',
    text: 'Nos reservamos el derecho de actualizar esta política de privacidad en cualquier momento. Los cambios serán publicados en esta página con la fecha de la última actualización.'
  }]
};
const termsContent = {
  title: 'Términos y Condiciones',
  sections: [{
    heading: 'Aceptación de los Términos',
    text: 'Al acceder y utilizar este sitio web y adquirir nuestros productos digitales, usted acepta estar sujeto a estos términos y condiciones. Si no está de acuerdo con alguna parte de estos términos, no debe utilizar nuestro sitio.'
  }, {
    heading: 'Productos Digitales',
    text: 'Todos los productos ofrecidos son digitales (eBooks, guías en PDF, hojas de trabajo). Una vez completada la compra, recibirá acceso inmediato para descargar los archivos. Los productos son para uso personal y no pueden ser redistribuidos, revendidos ni compartidos.'
  }, {
    heading: 'Política de Reembolso',
    text: 'Ofrecemos una garantía de satisfacción. Si no está satisfecho con su compra, puede solicitar un reembolso completo dentro de los 30 días posteriores a la compra, sin preguntas.'
  }, {
    heading: 'Propiedad Intelectual',
    text: 'Todo el contenido, incluyendo textos, gráficos, logotipos y materiales descargables, está protegido por derechos de autor. Queda prohibida la reproducción, distribución o modificación sin autorización expresa por escrito.'
  }, {
    heading: 'Descargo de Responsabilidad Médica',
    text: 'Los productos ofrecidos son exclusivamente para fines educativos e informativos. No constituyen consejo médico, diagnóstico ni tratamiento profesional. Siempre consulte a un profesional de la salud calificado antes de realizar cambios en su rutina de salud.'
  }, {
    heading: 'Limitación de Responsabilidad',
    text: 'No nos hacemos responsables de los resultados individuales obtenidos con el uso de nuestros productos. Los resultados pueden variar según cada persona. En ningún caso seremos responsables por daños directos, indirectos o consecuentes derivados del uso de nuestros productos.'
  }, {
    heading: 'Ley Aplicable',
    text: 'Estos términos se regirán e interpretarán de acuerdo con las leyes aplicables. Cualquier disputa será resuelta en los tribunales competentes de la jurisdicción correspondiente.'
  }]
};
type ModalType = 'privacy' | 'terms' | null;
function LegalModal({
  type,
  onClose
}: {
  type: 'privacy' | 'terms';
  onClose: () => void;
}) {
  const content = type === 'privacy' ? privacyContent : termsContent;
  return <motion.div className="fixed inset-0 z-50 flex items-center justify-center p-4" initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} transition={{
    duration: 0.2
  }} data-id="element-37">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} data-id="element-38" />

      {/* Modal */}
      <motion.div className="relative bg-[#FDF8F4] rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] overflow-hidden" initial={{
      scale: 0.95,
      y: 20
    }} animate={{
      scale: 1,
      y: 0
    }} exit={{
      scale: 0.95,
      y: 20
    }} transition={{
      type: 'spring',
      stiffness: 300,
      damping: 25
    }} data-id="element-39">
        {/* Header */}
        <div className="sticky top-0 bg-[#FDF8F4] border-b border-charcoal/10 px-6 py-4 flex items-center justify-between z-10" data-id="element-40">
          <h2 className="font-lora text-xl font-bold text-charcoal" data-id="element-41">
            {content.title}
          </h2>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-charcoal/5 transition-colors" aria-label="Cerrar" data-id="element-42">
            <XIcon className="w-5 h-5 text-charcoal/60" data-id="element-43" />
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-6 overflow-y-auto max-h-[calc(85vh-64px)]" data-id="element-44">
          <p className="font-inter text-sm text-charcoal/50 mb-6" data-id="element-45">
            Última actualización: Enero 2025
          </p>

          <div className="space-y-6" data-id="element-46">
            {content.sections.map((section, index) => <div key={index} data-id="element-47">
                <h3 className="font-inter font-semibold text-charcoal mb-2" data-id="element-48">
                  {section.heading}
                </h3>
                <p className="font-inter text-sm text-charcoal/75 leading-relaxed" data-id="element-49">
                  {section.text}
                </p>
              </div>)}
          </div>

          <div className="mt-8 pt-6 border-t border-charcoal/10" data-id="element-50">
            <p className="font-inter text-xs text-charcoal/40 text-center" data-id="element-51">
              Si tiene preguntas sobre estos términos, contáctenos a través de
              nuestro correo electrónico de soporte.
            </p>
          </div>
        </div>
      </motion.div>
    </motion.div>;
}
export function Footer() {
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  return <>
      <footer className="w-full bg-charcoal py-12" data-id="element-52">
        <div className="max-w-4xl mx-auto px-6 text-center" data-id="element-53">
          <p className="font-inter text-sm text-white/60 leading-relaxed" data-id="element-54">
            © 2025 Calmante Natural Ansiedad 24h. Todos los derechos
            reservados.
          </p>

          {/* Legal Links */}
          <div className="flex items-center justify-center gap-4 mt-4" data-id="element-55">
            <button onClick={() => setActiveModal('privacy')} className="font-inter text-xs text-white/40 hover:text-white/70 transition-colors underline underline-offset-2" data-id="element-56">
              Política de Privacidad
            </button>
            <span className="text-white/20" data-id="element-57">|</span>
            <button onClick={() => setActiveModal('terms')} className="font-inter text-xs text-white/40 hover:text-white/70 transition-colors underline underline-offset-2" data-id="element-58">
              Términos y Condiciones
            </button>
          </div>

          <p className="font-inter text-xs text-white/40 mt-4 max-w-2xl mx-auto" data-id="element-59">
            Descargo de responsabilidad: Este producto es solo para fines
            educativos e informativos. No sustituye el consejo médico
            profesional, diagnóstico o tratamiento. Siempre consulte a su médico
            u otro proveedor de atención médica calificado con cualquier
            pregunta que pueda tener sobre una condición médica.
          </p>
        </div>
      </footer>

      {/* Legal Modals */}
      <AnimatePresence data-id="element-60">
        {activeModal && <LegalModal type={activeModal} onClose={() => setActiveModal(null)} data-id="element-61" />}
      </AnimatePresence>
    </>;
}